import React, { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { API } from "aws-amplify";

import Button from '../../Button';
import SearchAddButton from '../../SearchAddButton';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faLock, faSearch, faTimes, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import '../../../css/elementary/manage_homerooms.css';

const displayTeacherName = (teacher) => {
	if(!teacher) return '';
	const firstName = (teacher.first_name) ? `${teacher.first_name}`.trim() : '';
	const lastName = (teacher.name) ? `${teacher.name}`.trim() : '';
	return `${lastName}, ${firstName}`.replace(/,\s*$/, '');
};

const sortTeachersByName = (teacherA, teacherB) => {
	const nameA = displayTeacherName(teacherA).toLowerCase();
	const nameB = displayTeacherName(teacherB).toLowerCase();
	if(nameA < nameB) return -1;
	if(nameA > nameB) return 1;
	return 0;
};

export default function SpecialsTeachers({ isLoading, subscreenLocked, subscreenRestricted, schoolInfo, teachers = [], setTeachers = ()=>{}, courses = [], setCourses = ()=>{}, handleMoveToNextScreen }) {
	const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
	const [teacherSearchOpen, setTeacherSearchOpen] = useState(false);
	const [teacherSearchResults, setTeacherSearchResults] = useState([]);
	const [courseSearchOpenByTeacher, setCourseSearchOpenByTeacher] = useState({});
	const [courseSearchResultsByTeacher, setCourseSearchResultsByTeacher] = useState({});

	const specialsCourses = useMemo(() => {
		return courses.filter(course => course.is_special === '1');
	}, [courses]);

	const specialsCourseIds = useMemo(() => {
		return new Set(specialsCourses.map(course => course.course_id));
	}, [specialsCourses]);

	const sortedTeachers = useMemo(() => {
		const teacherCopy = [...teachers];
		teacherCopy.sort(sortTeachersByName);
		return teacherCopy;
	}, [teachers]);

	const selectedTeachers = useMemo(() => {
		return selectedTeacherIds.map(teacherId => sortedTeachers.find(teacher => teacher.teacher_id === teacherId)).filter(Boolean);
	}, [selectedTeacherIds, sortedTeachers]);

	const getSpecialCourseIdsForTeacher = (teacher) => {
		if(!teacher) return [];
		const teacherCanTeach = Array.isArray(teacher.teacher_can_teach) ? teacher.teacher_can_teach : [];
		return teacherCanTeach.reduce((results, tctInfo) => {
			if(specialsCourseIds.has(tctInfo.course_id) && !results.includes(tctInfo.course_id)) results.push(tctInfo.course_id);
			return results;
		}, []);
	};

	const getAvailableTeachers = () => {
		return sortedTeachers;
	};

	const getAvailableSpecialsCourses = (teacherId) => {
		return specialsCourses;
	};

	const updateTeacherSpecialsCourses = async (teacherId, courseId, changeType) => {
		const teacherIndex = teachers.findIndex(teacher => teacher.teacher_id === teacherId);
		if(teacherIndex === -1) return;

		const teachersCopy = [...teachers];
		const teacherInfo = {...teachersCopy[teacherIndex]};
		const teacherCanTeach = Array.isArray(teacherInfo.teacher_can_teach) ? [...teacherInfo.teacher_can_teach] : [];
		const existingIndex = teacherCanTeach.findIndex(tctInfo => tctInfo.course_id === courseId);

		if(changeType === 'add')
		{
			if(existingIndex !== -1) return;
			teacherCanTeach.push({course_id:courseId, type:'1'});
		}
		else if(changeType === 'remove')
		{
			if(existingIndex === -1) return;
			teacherCanTeach.splice(existingIndex, 1);
		}
		else
		{
			return;
		}

		teacherInfo.teacher_can_teach = teacherCanTeach;
		teachersCopy[teacherIndex] = teacherInfo;
		setTeachers(teachersCopy);

		const course_id_to_update = `${courseId}`;
		const teacher_id_to_update = `${teacherId}`;
		const courses_copy = courses.map((course) => {
			if(`${course.course_id}` !== course_id_to_update) return course;

			const current_teachers_can_teach = (Array.isArray(course.teachers_can_teach)) ? [...course.teachers_can_teach] : [];
			const existing_teacher_index = current_teachers_can_teach.findIndex((tct_info) => `${tct_info.teacher_id}` === teacher_id_to_update);

			if(changeType === 'add' && existing_teacher_index === -1)
			{
				current_teachers_can_teach.push({teacher_id:teacher_id_to_update, type:'1'});
			}
			else if(changeType === 'remove' && existing_teacher_index !== -1)
			{
				current_teachers_can_teach.splice(existing_teacher_index, 1);
			}

			return {...course, teachers_can_teach: current_teachers_can_teach};
		});
		setCourses(courses_copy);

		await saveDataToDatabase({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'teacher',
			data_to_save: teacherInfo,
			database: 'elementary'
		});
	};

	const updateIsSpecialsTeacher = async (teacherId, isSpecialsTeacher) => {
		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			teacher_id: teacherId,
			is_specials_teacher: isSpecialsTeacher
		};
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/update-is-specials-teacher';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			await API.post(apiName, url, myInit);
			return true;
		} catch(e)
		{
			console.log(e.response);
			return false;
		}
	};

	const toggleTeacherSearch = () => {
		const newValue = !teacherSearchOpen;
		setTeacherSearchOpen(newValue);
		if(newValue) setTeacherSearchResults(getAvailableTeachers());
	};

	const fuzzySearchTeachers = (searchValue) => {
		const availableTeachers = getAvailableTeachers();
		const results = (searchValue.length > 0) ? (() => {
			const fuse = new Fuse(availableTeachers, {keys: ['first_name', 'name'], threshold: .1});
			return fuse.search(searchValue).map(result => result.item).slice(0, 8);
		})() : availableTeachers;

		setTeacherSearchResults(results);
	};

	const addTeacherCard = async (teacherId) => {
		if(selectedTeacherIds.includes(teacherId)) return;
		const statusUpdated = await updateIsSpecialsTeacher(teacherId, '1');
		if(!statusUpdated) return;

		setTeachers((currentTeachers) => currentTeachers.map((teacher) => {
			if(teacher.teacher_id !== teacherId) return teacher;
			return {...teacher, is_specials_teacher:'1'};
		}));

		setSelectedTeacherIds((currentTeacherIds) => {
			if(currentTeacherIds.includes(teacherId)) return currentTeacherIds;
			return [...currentTeacherIds, teacherId];
		});
	};

	const removeTeacherCard = async (teacherId) => {
		const teacherInfo = teachers.find(teacher => teacher.teacher_id === teacherId);
		if(!teacherInfo) return;
		const statusUpdated = await updateIsSpecialsTeacher(teacherId, '0');
		if(!statusUpdated) return;

		const teacherCanTeach = Array.isArray(teacherInfo.teacher_can_teach) ? teacherInfo.teacher_can_teach : [];
		const filteredTeacherCanTeach = teacherCanTeach.filter(tctInfo => !specialsCourseIds.has(tctInfo.course_id));

		const teachersCopy = teachers.map((teacher) => {
			if(teacher.teacher_id !== teacherId) return teacher;
			return {...teacher, teacher_can_teach: filteredTeacherCanTeach, is_specials_teacher:'0'};
		});

		setTeachers(teachersCopy);

		const teacher_id_to_remove = `${teacherId}`;
		const courses_copy = courses.map((course) => {
			if(`${course.is_special || '0'}` !== '1') return course;
			const current_teachers_can_teach = (Array.isArray(course.teachers_can_teach)) ? course.teachers_can_teach : [];
			const filtered_teachers_can_teach = current_teachers_can_teach.filter((tct_info) => `${tct_info.teacher_id}` !== teacher_id_to_remove);
			if(filtered_teachers_can_teach.length === current_teachers_can_teach.length) return course;
			return {...course, teachers_can_teach: filtered_teachers_can_teach};
		});
		setCourses(courses_copy);

		setSelectedTeacherIds(selectedTeacherIds.filter(tempTeacherId => tempTeacherId !== teacherId));

		const openCopy = {...courseSearchOpenByTeacher};
		const resultsCopy = {...courseSearchResultsByTeacher};
		delete openCopy[teacherId];
		delete resultsCopy[teacherId];
		setCourseSearchOpenByTeacher(openCopy);
		setCourseSearchResultsByTeacher(resultsCopy);

		saveDataToDatabase({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'teacher',
			data_to_save: {...teacherInfo, teacher_can_teach: filteredTeacherCanTeach, is_specials_teacher:'0'},
			database: 'elementary'
		});
	};

	const toggleCourseSearch = (teacherId) => {
		const newValue = !courseSearchOpenByTeacher[teacherId];
		const openCopy = {...courseSearchOpenByTeacher, [teacherId]: newValue};
		const resultsCopy = {...courseSearchResultsByTeacher};
		if(newValue) resultsCopy[teacherId] = getAvailableSpecialsCourses(teacherId);
		setCourseSearchOpenByTeacher(openCopy);
		setCourseSearchResultsByTeacher(resultsCopy);
	};

	const fuzzySearchCourses = (teacherId, searchValue) => {
		const availableCourses = getAvailableSpecialsCourses(teacherId);
		const results = (searchValue.length > 0) ? (() => {
			const fuse = new Fuse(availableCourses, {keys: ['name', 'course_code'], threshold: .1});
			return fuse.search(searchValue).map(result => result.item).slice(0, 8);
		})() : availableCourses;

		setCourseSearchResultsByTeacher({...courseSearchResultsByTeacher, [teacherId]: results});
	};

	useEffect(() => {
		const teacherIdsWithSpecials = sortedTeachers.reduce((results, teacher) => {
			const specialCourseIds = getSpecialCourseIdsForTeacher(teacher);
			const teacherMarkedAsSpecials = `${teacher.is_specials_teacher || '0'}` === '1';
			if(specialCourseIds.length > 0 || teacherMarkedAsSpecials) results.push(teacher.teacher_id);
			return results;
		}, []);

		setSelectedTeacherIds((previousSelectedTeacherIds) => {
			const existingValidIds = previousSelectedTeacherIds.filter(teacherId => sortedTeachers.some(teacher => teacher.teacher_id === teacherId));
			const mergedIds = [...new Set([...existingValidIds, ...teacherIdsWithSpecials])];
			return mergedIds;
		});
	}, [sortedTeachers, specialsCourses]);

	return (
		<div className='mb-elem-container'>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		) : (subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		) : (isLoading) ?
		(
			<div className='center-text'>
				<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'25px'}}/>
			</div>
		) :
		(
			<>
				<h1 className='progress-main-header'>Specials Teachers</h1>
				<p className='progress-subheader elem-manage-homerooms-subheader'>Add each specials teacher as a card, then assign the specials courses they can teach.</p>

				<div className='elem-manage-homerooms-grid'>
					{selectedTeachers.map((teacherInfo) => {
						const teacherId = teacherInfo.teacher_id;
						const courseSearchOpen = !!courseSearchOpenByTeacher[teacherId];
						const courseResults = courseSearchResultsByTeacher[teacherId] || [];
						const teacherSpecialsCourseIds = getSpecialCourseIdsForTeacher(teacherInfo);
						const teacherSpecialsCourses = teacherSpecialsCourseIds.reduce((results, courseId) => {
							const courseInfo = courses.find(course => course.course_id === courseId);
							if(courseInfo) results.push(courseInfo);
							return results;
						}, []).sort((courseA, courseB) => `${courseA.name || ''}`.localeCompare(`${courseB.name || ''}`));

						return (
							<div className='elem-manage-homerooms-grade-card' key={teacherId}>
								<div className='elem-manage-homerooms-grade-header'>
									<div className='elem-manage-homerooms-grade-heading'>
										<div className='elem-manage-homerooms-grade-kicker'>Specials Teacher</div>
										<div className='elem-manage-homerooms-grade-title'>{displayTeacherName(teacherInfo)}</div>
										<div className='elem-manage-homerooms-grade-meta'>
											<div className='elem-manage-homerooms-grade-count-badge'>{teacherSpecialsCourseIds.length} {teacherSpecialsCourseIds.length === 1 ? 'course' : 'courses'}</div>
										</div>
									</div>
									<div className='elem-manage-homerooms-grade-actions'>
										<FontAwesomeIcon className='gray-to-red-link cursor-pointer' icon={faTrashCan} onClick={() => removeTeacherCard(teacherId)}/>
									</div>
								</div>

								<div className='elem-manage-homerooms-card-body elem-specials-teachers-course-list'>
									{teacherSpecialsCourseIds.length === 0 &&
										<div className='specials-setup-display elem-manage-homerooms-display'>No specials courses added yet...</div>
									}
									{teacherSpecialsCourses.map((courseInfo) => {
										const courseId = courseInfo.course_id;
										const courseName = courseInfo.name;
										const courseCode = courseInfo.course_code;

										return (
											<div className='specials-setup-display elem-manage-homerooms-display' key={`${teacherId}-${courseId}`}>
												<div>{courseName} {courseCode && `(${courseCode})`}</div>
												<div className='cursor-pointer' onClick={() => updateTeacherSpecialsCourses(teacherId, courseId, 'remove')}><FontAwesomeIcon icon={faTimes}/></div>
											</div>
										);
									})}
								</div>

								{courseSearchOpen &&
									<div className='search-add-row-container elem-manage-homerooms-search-row'>
										<div className='search-add-search-container'>
											<div className='search-add-adding-input-container'>
												<input className='search-add-adding-input' onChange={(e) => fuzzySearchCourses(teacherId, e.target.value)} placeholder='Search for course' autoFocus={true}/>
												<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
											</div>
											<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleCourseSearch(teacherId)}/>
										</div>
										{courseResults.length > 0 &&
											<div className='elem-suggestion-box-parent parent-div'>
												<div className='elem-suggestion-box'>
													{courseResults.map((searchResult, index) => {
														const courseSelected = teacherSpecialsCourseIds.includes(searchResult.course_id);
														return (
															<div className='search-add-search-result' key={`${teacherId}-${searchResult.course_id}-${index}`}>
																<div>{searchResult.name} {searchResult.course_code && `(${searchResult.course_code})`}</div>
																<SearchAddButton selected={courseSelected} onAdd={() => updateTeacherSpecialsCourses(teacherId, searchResult.course_id, 'add')} />
															</div>
														);
													})}
												</div>
											</div>
										}
									</div>
								}

								<div className='blue-link medium-text specials-setup-add-link' onClick={() => toggleCourseSearch(teacherId)}>
									<FontAwesomeIcon icon={faCirclePlus}/>
									<span>Add Course</span>
								</div>
							</div>
						);
					})}

					{!teacherSearchOpen ?
						(
							<div className='elem-manage-homerooms-add-grade-card cursor-pointer' onClick={toggleTeacherSearch}>
								<FontAwesomeIcon icon={faCirclePlus}/>
								<div>Add A Teacher</div>
							</div>
						) :
						(
							<div className='elem-manage-homerooms-grade-card elem-manage-homerooms-new-grade-card'>
								<div className='specials-setup-label'>Add Teacher</div>
								<div className='search-add-row-container elem-manage-homerooms-search-row'>
									<div className='search-add-search-container'>
										<div className='search-add-adding-input-container'>
											<input className='search-add-adding-input' onChange={(e) => fuzzySearchTeachers(e.target.value)} placeholder='Search for teacher' autoFocus={true}/>
											<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
										</div>
										<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={toggleTeacherSearch}/>
									</div>
									{teacherSearchResults.length > 0 &&
										<div className='elem-suggestion-box-parent parent-div'>
											<div className='elem-suggestion-box'>
												{teacherSearchResults.map((searchResult, index) => {
													const teacherSelected = selectedTeacherIds.includes(searchResult.teacher_id);
													return (
														<div className='search-add-search-result' key={`${searchResult.teacher_id}-${index}`}>
															<div>{displayTeacherName(searchResult)}</div>
															<SearchAddButton selected={teacherSelected} onAdd={() => addTeacherCard(searchResult.teacher_id)} />
														</div>
													);
												})}
											</div>
										</div>
									}
								</div>
							</div>
						)
					}
				</div>

				<div className='fixed-bottom-save-container'>
					<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Place Specials' onClickFunction={handleMoveToNextScreen} />
				</div>
			</>
		)}
		</div>
	);
}
